import { useMemo } from 'react';
import { ChevronDown, ChevronUp, Pencil, X } from 'lucide-react';
import cn from 'classnames';

import { SelectInput } from '../../../inputs/SelectInput';

import {
  introducerSelectData,
  raltionsSelectData,
  roleSelectData,
} from '../../../../../lib/mocks/add-contact-mock';

// "None" is expressed by clearing the select, so drop the '0' entry and keep values numeric
const toOptions = data =>
  data
    .filter(option => Number(option.value) !== 0)
    .map(option => ({ ...option, value: Number(option.value) }));

const linkFields = [
  { name: 'role', label: 'Role', options: toOptions(roleSelectData) },
  {
    name: 'relations',
    label: 'Source',
    options: toOptions(raltionsSelectData),
  },
  {
    name: 'introducer_type',
    label: 'Introducer',
    options: toOptions(introducerSelectData),
  },
];

const getInitials = ({ first_name, last_name }) =>
  [first_name, last_name]
    .filter(Boolean)
    .map(word => word.trim()[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const getSummary = link =>
  linkFields
    .map(
      field =>
        field.options.find(option => option.value === link[field.name])?.title
    )
    .filter(Boolean)
    .join(' · ') || 'No role set';

const LinkSelect = ({ field, value, onChange }) => {
  const initialSelected = useMemo(
    () => field.options.filter(option => option.value === value),
    [field.options, value]
  );

  return (
    <div className="grid grid-cols-[76px_minmax(0,1fr)] items-center gap-2.5">
      <span className="text-xs text-[#6b6b6b]">{field.label}</span>

      <SelectInput
        options={field.options}
        initialSelected={initialSelected}
        placeholder={field.label}
        onSelect={([item]) => onChange(field.name, item.value)}
        onRemove={() => onChange(field.name, null)}
      />
    </div>
  );
};

export const ContactsItem = ({
  contact = {},
  link = {},
  isOpen = false,
  isEditable = false,
  handleToggle = () => {},
  handleEdit = () => {},
  handleRemove = () => {},
  handleLinkChange = () => {},
}) => {
  const { first_name, last_name, id, masked } = contact;

  const handleRemoveClick = e => {
    e.stopPropagation();
    handleRemove(id);
  };

  const handleHeaderKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(id);
    }
  };

  return (
    <div className="w-full bg-white border border-[#e2e2e2] rounded-md">
      {/* BE re-attaches masked contacts on save, so remove/role edits would be ignored */}
      <div
        role={masked ? undefined : 'button'}
        tabIndex={masked ? undefined : 0}
        aria-expanded={masked ? undefined : isOpen}
        className={cn('flex items-center gap-2.5 py-2.5 pr-2 pl-3 rounded-md', {
          'cursor-pointer hover:bg-[#fafafa]': !masked,
        })}
        onClick={masked ? undefined : () => handleToggle(id)}
        onKeyDown={masked ? undefined : handleHeaderKeyDown}
      >
        <div className="flex flex-none items-center justify-center w-8 h-8 rounded-full bg-[#e3e5f3] text-[13px] font-semibold text-[#5b63a8]">
          {masked ? '***' : getInitials(contact)}
        </div>

        <div className="flex flex-col flex-1 gap-0.5 min-w-0">
          <span className="text-sm font-semibold text-[#222] truncate">
            {masked
              ? 'Contact of another agent'
              : `${first_name ? `${first_name} ` : ''}${last_name || ''}`}
          </span>
          <span className="text-xs text-[#666] truncate">
            {getSummary(link)}
          </span>
        </div>

        {!masked && (
          <>
            <span className="px-1 text-[#777]">
              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>

            <button
              type="button"
              title="Remove contact"
              className="flex flex-none items-center justify-center w-7 h-7 rounded text-[#777] hover:bg-[#f3e3e3] hover:text-[#a33]"
              onClick={handleRemoveClick}
            >
              <X size={16} />
            </button>
          </>
        )}
      </div>

      {isOpen && !masked && (
        <div className="flex flex-col gap-2 pt-2.5 px-3 pb-3 border-t border-[#ececec]">
          {linkFields.map(field => (
            <LinkSelect
              key={field.name}
              field={field}
              value={link[field.name] ?? null}
              onChange={(name, value) => handleLinkChange(id, name, value)}
            />
          ))}

          {isEditable && (
            <button
              type="button"
              className="flex items-center gap-1 self-end text-xs text-[#5b63a8] hover:underline"
              onClick={() => handleEdit(id)}
            >
              <Pencil size={12} />
              Edit contact details
            </button>
          )}
        </div>
      )}
    </div>
  );
};
